import { Alert, AlertDescription, AlertIcon, Box, Button, Flex, Heading, Text } from '@chakra-ui/react';
import { FunctionComponent } from 'react';
import { useAuth } from '@/auth';
import { StepPageLayout } from '@/layouts/StepPageLayout';
import { usePublishMetaMutation } from '@/queries';
import { useAppSelector } from '@/redux';
import { getMetaProcessingErrorMessage, parseMeta } from '@/services/metaProcessingApi';
import { PUBLISH_QA_STEP } from './constants';

export const PublishQaPage: FunctionComponent = () => {
  const { user } = useAuth();
  const selectedClient = useAppSelector((state) => state.wave.selectedClient);
  const selectedWaveOption = useAppSelector((state) => state.wave.selectedWaveOption);
  // The processed meta as reviewed (and possibly edited) on the Meta Processing step.
  const processedMeta = useAppSelector((state) => state.wave.processedMeta);
  const publishMutation = usePublishMetaMutation();

  if (!selectedClient) {
    return (
      <Box p={8}>
        <Text color="marketcast.text.primary">Client not found</Text>
      </Box>
    );
  }

  const hasMeta = processedMeta.trim().length > 0;
  const canPublish = Boolean(user?.email) && hasMeta && !publishMutation.isPending && !publishMutation.isSuccess;

  const handlePublish = () => {
    // Already validated when the user confirmed the review; re-checked here because this is
    // the last point before it overwrites the meta the QA dashboards read.
    const meta = parseMeta(processedMeta);
    if (!user?.email || !meta) return;
    publishMutation.mutate({ userEmail: user.email, groupId: selectedClient.groupId, meta });
  };

  return (
    <StepPageLayout
      currentStep={PUBLISH_QA_STEP}
      // Last step: there is no Next.
      isNextEnabled={false}
      isBackEnabled={!publishMutation.isPending}
    >
      <Flex p={6} justify="center">
        <Flex
          direction="column"
          gap={5}
          w="100%"
          maxW="720px"
          bg="marketcast.base.backgroundAlt"
          borderRadius="12px"
          border="1px solid"
          borderColor="marketcast.gray.5"
          boxShadow="sm"
          p={6}
        >
          <Heading as="h2" fontSize="16px" fontWeight="600" color="marketcast.text.primary">
            Publish to QA
          </Heading>

          {hasMeta ? (
            <Alert status="info" borderRadius="8px">
              <AlertIcon />
              <AlertDescription fontSize="14px">
                {`You've reviewed the processed meta for ${selectedClient.groupName} (${selectedWaveOption}). Publishing uploads it to QA and replaces the meta currently there.`}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert status="warning" borderRadius="8px">
              <AlertIcon />
              <AlertDescription fontSize="14px">
                There is no processed meta to publish yet. Go back to Meta Processing to generate and review it.
              </AlertDescription>
            </Alert>
          )}

          {publishMutation.isSuccess && (
            <Alert status="success" borderRadius="8px">
              <AlertIcon />
              <AlertDescription fontSize="14px">Published to QA.</AlertDescription>
            </Alert>
          )}

          {publishMutation.isError && (
            <Text fontSize="sm" color="red.500" role="alert">
              {`Couldn't publish to QA: ${getMetaProcessingErrorMessage(publishMutation.error)}`}
            </Text>
          )}

          <Box>
            <Button
              variant={canPublish ? 'solid' : 'outline'}
              onClick={handlePublish}
              isLoading={publishMutation.isPending}
              loadingText="Publishing..."
              isDisabled={!canPublish}
            >
              Publish to QA
            </Button>
          </Box>
        </Flex>
      </Flex>
    </StepPageLayout>
  );
};
